import React from "react";
import { Field, ConfigFields } from "./Properties";
import {
  GATES,
  SEQUENTIAL,
  pins,
  isSequential,
  pinLabel,
  allSignals,
  sourceKey,
  signalName,
  nodeSize,
  LABELS,
} from "../engine/registry";
import { changeComponent, connect } from "../utils/editing";
import { normalizeClocks, renamePin } from "../utils/circuitEditing";
import { clone } from "../utils/projects";
export function Dialog({ title, children, onClose, wide = false, footer }) {
  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`modal ${wide ? "wide-modal" : ""}`}
        onKeyDown={(e) => {
          if (e.key === "Escape") onClose();
        }}
      >
        <header className="modal-header">
          <h2>{title}</h2>
          <button aria-label="Close dialog" onClick={onClose}>
            ×
          </button>
        </header>
        <div className="modal-body">{children}</div>
        <footer className="modal-footer">
          {footer || (
            <button className="primary" onClick={onClose}>
              Done
            </button>
          )}
        </footer>
      </section>
    </div>
  );
}
export default function ComponentDialog({
  project,
  id,
  onChange,
  onClose,
  notify,
}) {
  const c = project.components.find((c) => c.id === id);
  if (!c) return null;
  const update = (patch) => {
    try {
      onChange(normalizeClocks(changeComponent(project, id, patch)));
    } catch (e) {
      notify(e.message, true);
    }
  };
  const z = nodeSize(c),
    sources = allSignals(project).filter((s) => !s.terminal);
  const types = GATES.includes(c.type)
    ? GATES
    : isSequential(c)
      ? SEQUENTIAL
      : [c.type];
  return (
    <Dialog title={`Edit ${LABELS[c.type]}`} onClose={onClose} wide>
      <div className="component-edit-grid">
        <section>
          <Field label="Instance name">
            <input
              value={c.name}
              maxLength="32"
              onChange={(e) => update({ name: e.target.value })}
            />
          </Field>
          {types.length > 1 && (
            <Field label="Component type">
              <select
                value={c.type}
                onChange={(e) =>
                  update({
                    type: e.target.value,
                    config: {
                      ...c.config,
                      inputs:
                        e.target.value === "NOT"
                          ? 1
                          : Math.max(2, c.config.inputs),
                    },
                  })
                }
              >
                {types.map((t) => (
                  <option key={t} value={t}>
                    {LABELS[t]}
                  </option>
                ))}
              </select>
            </Field>
          )}
          <ConfigFields
            node={c}
            update={(patch) => update({ config: { ...c.config, ...patch } })}
          />
          <div className="field-pair">
            <Field label="Diagram width">
              <input
                type="number"
                min="24"
                max="800"
                value={z.w}
                onChange={(e) =>
                  update({ size: { w: Math.max(24, +e.target.value), h: z.h } })
                }
              />
            </Field>
            <Field label="Diagram height">
              <input
                type="number"
                min="24"
                max="1200"
                value={z.h}
                onChange={(e) =>
                  update({ size: { w: z.w, h: Math.max(24, +e.target.value) } })
                }
              />
            </Field>
          </div>
          {isSequential(c) && (
            <button onClick={() => update({ size: { w: 75, h: 120 } })}>
              Restore 2.5 : 4 proportions
            </button>
          )}
        </section>
        <section>
          <h3>Input terminals & connections</h3>
          {pins(c).inputs.map((pin) => {
            const e = project.connections.find(
              (e) => e.to.component === id && e.to.pin === pin.id,
            );
            return (
              <div className="pin-edit" key={pin.id}>
                <Field label={`${pin.id} label`}>
                  <input
                    value={pinLabel(c, pin.id)}
                    maxLength="24"
                    onChange={(evt) =>
                      onChange(renamePin(project, id, pin.id, evt.target.value))
                    }
                  />
                </Field>
                <Field label={`${pinLabel(c, pin.id)} source`}>
                  <select
                    value={e ? sourceKey(e.from) : ""}
                    onChange={(evt) => {
                      const s = sources.find((s) => s.id === evt.target.value);
                      try {
                        if (s)
                          onChange(
                            normalizeClocks(
                              connect(project, s.source, {
                                component: id,
                                pin: pin.id,
                              }),
                            ),
                          );
                        else {
                          const p = clone(project);
                          p.connections = p.connections.filter(
                            (w) => w.id !== e?.id,
                          );
                          onChange(p);
                        }
                      } catch (error) {
                        notify(error.message, true);
                      }
                    }}
                  >
                    <option value="">Unconnected</option>
                    {sources.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
            );
          })}
          <h3>Timing diagram outputs</h3>
          {pins(c).outputs.map((pin) => {
            const source = { component: id, pin: pin.id },
              key = sourceKey(source),
              checked = project.observables.some(
                (o) => sourceKey(o.source) === key,
              );
            return (
              <label className="check" key={key}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => {
                    const p = clone(project);
                    p.observables = p.observables.filter(
                      (o) => sourceKey(o.source) !== key,
                    );
                    if (e.target.checked)
                      p.observables.push({ id: key, source });
                    onChange(p);
                  }}
                />
                {signalName(project, source)}
              </label>
            );
          })}
        </section>
      </div>
    </Dialog>
  );
}
export function TerminalDialog({ project, onChange, onClose, onFillMissing }) {
  const states = project.components.filter(isSequential);
  return (
    <Dialog
      title="Name the terminals"
      onClose={onClose}
      wide
      footer={
        <>
          <button onClick={onFillMissing}>Assign missing data terminals</button>
          <button className="primary" onClick={onClose}>
            Done · return to circuit
          </button>
        </>
      }
    >
      <p className="helper">
        These labels are used in the timing simulator. Gate inputs remain
        editable signals; flip-flop input labels follow the wires connected to
        them.
      </p>
      <div className="component-edit-grid">
        <section>
          <h3>Primary input variables</h3>
          {project.inputs.map((input) => (
            <Field key={input.id} label={`Input ${input.id}`}>
              <input
                aria-label={`Terminal ${input.id} name`}
                value={input.name}
                maxLength="24"
                onChange={(e) => {
                  const p = clone(project);
                  p.inputs.find((i) => i.id === input.id).name = e.target.value;
                  onChange(p);
                }}
              />
            </Field>
          ))}
          {!project.inputs.length && (
            <p className="helper">
              Connect a named terminal from the component library, or assign
              missing data terminals.
            </p>
          )}
        </section>
        <section>
          <h3>Flip-flop & latch terminals</h3>
          {states.map((c) => (
            <div className="terminal-card" key={c.id}>
              <Field label={`${c.name} output variable`}>
                <input
                  value={c.config.output || c.name}
                  maxLength="24"
                  onChange={(e) => {
                    const p = clone(project);
                    p.components.find((n) => n.id === c.id).config.output =
                      e.target.value;
                    onChange(p);
                  }}
                />
              </Field>
              <div className="terminal-label-grid">
                {pins(c).inputs.map((pin) => (
                  <Field key={pin.id} label={`${c.name}.${pin.id}`}>
                    <input
                      aria-label={`${c.name}.${pin.id} label`}
                      value={pinLabel(c, pin.id)}
                      maxLength="24"
                      onChange={(e) =>
                        onChange(
                          renamePin(project, c.id, pin.id, e.target.value),
                        )
                      }
                    />
                  </Field>
                ))}
              </div>
            </div>
          ))}
        </section>
      </div>
    </Dialog>
  );
}
